import { useAuth } from '@/context/Auth'
import { Box, Typography } from '@mui/material'
import { intersection } from 'lodash'
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useCategories } from '@/api/categories/categories'
import { useDeleteEducationModule } from '@/api/education-modules/delete-module'
import { EducationModule, useEducationModules } from '@/api/education-modules/modules'
import { useRecommendedModulesByName } from '@/api/education-modules/recommended-modules'
import { useToggleSavedContent } from '@/api/profile/toggle-saved-asset'
import { useToggleSavedAssetPackage } from '@/api/profile/toggle-saved-asset-package'
import { useVideosPackages } from '@/api/videos/packages/get'
import questionImg from '@/assets/images/popup/question.svg'
import Alert from '@/common/components/Alert/Alert'
import LaunchWizard from '@/common/components/LaunchWizard/LaunchWizard'
import { LayoutContext } from '@/common/components/Layout/Layout'
import LoadingContainer from '@/common/components/LoadingContainer/LoadingContainer'
import NoResultsContainer from '@/common/components/NoResultsContainer/NoResultsContainer'
import Popup from '@/common/components/Popup'
import useNavigateWithLayoutBlocker from '@/common/hooks/useNavigateWithLayoutBlocker'
import useToast from '@/common/hooks/useToast'
import { PhishingSimulationPackage } from '@/types/phishingSimulations'
import { VideosPackageResponse } from '@/types/videos'
import { useSearchParams } from 'react-router-dom'
import { ContentLibraryContext } from '../ContentLibrary'
import PackagesCaruselView from '../SimulationsLibrary/PackagesCaruselView'
import CreateVideoModal from './CreateVideoModal/CreateVideoModal'
import VideoLibraryCardView from './VideoLibraryCardView'
import VideoLibraryTable from './VideoLibraryTable'
import VideoPreviewModal from './VideoPreviewModal'

type VideosLibraryProps = {
  isTableView: boolean
  isVideoPackageModalOpen: boolean
  setIsVideoPackageModalOpen: (isOpen: boolean) => void
  queryFilters?: {
    name?: string
    languages?: string[]
    subtitles?: string[]
    tags?: string[]
  }
}

const RECOMMENDED_MODULES_PLAYLIST = 'recommended_training_playlist'

const VideosLibrary: FC<VideosLibraryProps> = ({
  queryFilters,
  isTableView,
  isVideoPackageModalOpen,
  setIsVideoPackageModalOpen,
}) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { setTempSimulationPackage, setTempSimulationPackageId, setTempSimulationPackageName } =
    useContext(LayoutContext)
  const { setIsMultiSimulationSideMenuOpen, isMultiSimulationSideMenuOpen, setMultiSimulationType } =
    useContext(ContentLibraryContext)

  const { data: videos = [], isLoading: isPendingVideos } = useEducationModules()

  const { data: recommendedVideos = [] } = useRecommendedModulesByName(RECOMMENDED_MODULES_PLAYLIST)
  const { user, user: { saved_assets = [], saved_asset_packages = [] } = {} } = useAuth()
  const { data: categoryData } = useCategories()

  const { mutateAsync: toggleSavedAssetPackage } = useToggleSavedAssetPackage()

  const [deleteEducationModule] = useDeleteEducationModule()
  const [saveAsset] = useToggleSavedContent()
  const { errorToast, successToast } = useToast()
  const [previewModule, setPreviewModule] = useState<EducationModule | undefined>()
  const [previewModalIsOpen, setPreviewModalIsOpen] = useState(false)
  const [launchWizardIsOpen, setLaunchWizardIsOpen] = useState(false)

  const [moduleForDeletion, setModuleForDeletion] = useState<EducationModule | null>(null)
  const [deleteVideoAlertIsOpen, setDeleteVideoAlertIsOpen] = useState(false)
  const [editModule, setEditModule] = useState<EducationModule | undefined>()
  const [editModuleIsOpen, setEditModuleIsOpen] = useState(false)

  const { data: videosPackagesData, isPending: isVideosPackagesPending } = useVideosPackages()

  const isFirstVideoPackageModalOpen = useMemo(() => {
    const isFirstVideoPackage = !videosPackagesData?.find((videoPackage) => videoPackage.created_by === user?.id)
    if (!isFirstVideoPackage) setIsVideoPackageModalOpen(false)
    return isFirstVideoPackage
  }, [videosPackagesData, isVideoPackageModalOpen])

  const navigateTo = useNavigateWithLayoutBlocker()

  const handleVideosPackagePreview = useCallback(
    (videoPackage: VideosPackageResponse | PhishingSimulationPackage) => {
      navigateTo(`/content-library/videos-package/${videoPackage.id}`)
    },
    [navigateTo]
  )

  const groupedVideosByCategory: {
    [topic: string]: EducationModule[]
  } = useMemo(() => {
    const groupedVideosByTopic: { [topic: string]: EducationModule[] } = {}

    if (Array.isArray(recommendedVideos) && recommendedVideos.length > 0) {
      const publicVideos = recommendedVideos.filter((video) => {
        return !video.client_id
      })

      if (publicVideos.length > 0) {
        groupedVideosByTopic['Recommended Playlist'] = publicVideos
      }
    }

    if (!Array.isArray(videos)) {
      return groupedVideosByTopic
    }

    if (!categoryData) {
      groupedVideosByTopic['All'] = videos
      return groupedVideosByTopic
    }

    categoryData.categories.forEach((category) => {
      groupedVideosByTopic[category.name] = []
    })

    const categorizedVideoIds = new Set<string>()

    videos.forEach((video) => {
      for (const tag of video.tags) {
        const categories = categoryData.categories_by_tag[tag] || []
        categories.forEach(({ name }) => {
          if (!groupedVideosByTopic[name]) {
            groupedVideosByTopic[name] = []
          }
          const existingVideos = groupedVideosByTopic[name].map((video) => video._id)
          if (!existingVideos.includes(video._id)) {
            groupedVideosByTopic[name].push(video)
            categorizedVideoIds.add(video._id)
          }
        })
      }
    })

    // Add uncategorized videos to an "Other" category
    const uncategorizedVideos = videos.filter((video) => !categorizedVideoIds.has(video._id))
    if (uncategorizedVideos.length > 0) {
      groupedVideosByTopic['Other'] = uncategorizedVideos
    }

    return groupedVideosByTopic
  }, [categoryData, videos, recommendedVideos])

  const matchTopicFilter = useCallback(
    (topic: string) => {
      return (
        !queryFilters?.tags?.length ||
        (queryFilters?.tags?.length && queryFilters?.tags?.length > 0 && queryFilters.tags.includes(topic))
      )
    },
    [queryFilters]
  )

  const matchTagFilter = useCallback(
    (tags: string[]) => {
      return (
        !queryFilters?.tags?.length ||
        (queryFilters?.tags?.length &&
          queryFilters?.tags?.length > 0 &&
          intersection(tags, queryFilters.tags).length > 0)
      )
    },
    [queryFilters]
  )

  const matchTitleFilter = useCallback(
    (title: string, description?: string) => {
      return (
        !queryFilters?.name ||
        title.toLowerCase().includes(queryFilters?.name.toLowerCase()) ||
        (description && description.toLowerCase().includes(queryFilters?.name.toLowerCase()))
      )
    },
    [queryFilters]
  )

  const matchLanguageFilter = useCallback(
    (languages: string[], key: 'languages' | 'subtitles' = 'languages') => {
      return !queryFilters?.[key]?.length || intersection(languages, queryFilters?.[key]).length > 0
    },
    [queryFilters]
  )

  const filteredVideosPackages = useMemo(() => {
    if (!videosPackagesData) return []

    return videosPackagesData.filter((videoPackage) => {
      const packageSubtitles = videoPackage.videos.reduce((acc: string[], video) => {
        const subtitleLanguages = Object.keys(video.subtitle_tracks || {})
        return [...acc, ...subtitleLanguages]
      }, [])

      const uniqueSubtitles = [...new Set(packageSubtitles)]

      return (
        matchTitleFilter(videoPackage.name) &&
        matchLanguageFilter(videoPackage.languages) &&
        matchTagFilter(videoPackage.tags) &&
        matchLanguageFilter(uniqueSubtitles, 'subtitles')
      )
    })
  }, [videosPackagesData, matchTitleFilter, matchLanguageFilter, matchTagFilter, matchLanguageFilter, queryFilters])

  const filteredVideos: {
    [topic: string]: EducationModule[]
  } = useMemo(() => {
    const filteredVideoGroups: { [topic: string]: EducationModule[] } = {}
    if (!isTableView) {
      Object.entries(groupedVideosByCategory).forEach(([topic, videos]) => {
        const filteredVideos = videos.filter(
          (video) =>
            matchTagFilter(video.tags) &&
            matchTitleFilter(video.title, video.description) &&
            matchLanguageFilter(video.languages) &&
            matchLanguageFilter(Object.keys(video?.subtitle_tracks || {}), 'subtitles')
        )
        if (filteredVideos.length > 0) {
          filteredVideoGroups[topic] = filteredVideos
        }
      })
    } else {
      filteredVideoGroups['all'] = videos.filter(
        (video) =>
          matchTitleFilter(video.title) &&
          matchLanguageFilter(video.languages) &&
          matchLanguageFilter(video?.subtitle_tracks ? Object.keys(video.subtitle_tracks) : [], 'subtitles') &&
          matchTagFilter(video.tags)
      )
    }

    return filteredVideoGroups
  }, [
    isTableView,
    groupedVideosByCategory,
    matchTopicFilter,
    matchTitleFilter,
    matchLanguageFilter,
    videos,
    matchTagFilter,
  ])

  const handleOpenPreviewModal = (video: EducationModule) => {
    setPreviewModule(video)
    setPreviewModalIsOpen(true)
  }

  const handleOpenLaunchWizard = (video: EducationModule) => {
    setPreviewModule(video)
    setLaunchWizardIsOpen(true)
  }

  const openDeleteVideoAlert = (video: EducationModule) => {
    setModuleForDeletion(video)
    setDeleteVideoAlertIsOpen(true)
  }

  const closeDeleteVideoAlert = () => {
    setModuleForDeletion(null)
    setDeleteVideoAlertIsOpen(false)
  }

  async function handleDeleteModule() {
    if (!moduleForDeletion) return
    try {
      await deleteEducationModule(moduleForDeletion.id)
      setPreviewModalIsOpen(false)
      successToast(t('videoLibrary.deleted'))
    } catch (error) {
      errorToast(t('videoLibrary.errors.failedToDeleteModule'))
    }
  }

  const handleLaunchModule = async (module: EducationModule) => {
    handleOpenLaunchWizard(module)
  }

  const handleEditModule = async (module: EducationModule) => {
    setEditModule(module)
    setEditModuleIsOpen(true)
    setPreviewModalIsOpen(false)
  }

  const handleSaveModule = async (moduleId: string) => {
    try {
      await saveAsset(moduleId)
    } catch (error) {
      errorToast(t('videoLibrary.errors.failedToSaveModule'))
    }
  }

  const handleSaveModuleFromPreview = (module: EducationModule) => {
    handleSaveModule(module.id)
  }

  const handlePackageEdit = (videoPackage: VideosPackageResponse) => {
    setTempSimulationPackageId(videoPackage.id)
    setTempSimulationPackageName(videoPackage.name)
    setTempSimulationPackage([...videoPackage.videos])
    setIsMultiSimulationSideMenuOpen(true)
    setMultiSimulationType('videos')
  }

  const editPackage = searchParams.get('editPackage')

  useEffect(() => {
    if (editPackage) {
      const packageFound = videosPackagesData?.find((videoPackage) => videoPackage.id === editPackage)

      if (packageFound) {
        handlePackageEdit({ ...packageFound })
      }
    }
  }, [editPackage, videosPackagesData])

  return (
    <Box>
      <VideoPreviewModal
        video={previewModule}
        open={previewModalIsOpen}
        handleClose={() => setPreviewModalIsOpen(false)}
        deleteModule={openDeleteVideoAlert}
        launchModule={handleLaunchModule}
        editModule={handleEditModule}
        saveModule={handleSaveModuleFromPreview}
        isSaved={previewModule ? saved_assets?.includes(previewModule.id) : false}
      />
      {previewModule && (
        <LaunchWizard open={launchWizardIsOpen} onClose={() => setLaunchWizardIsOpen(false)} assets={[previewModule]} />
      )}
      <CreateVideoModal
        hideButton
        isModalOpen={editModuleIsOpen}
        setIsModalOpen={setEditModuleIsOpen}
        video={editModule}
      />
      <Popup
        open={isVideoPackageModalOpen && isFirstVideoPackageModalOpen}
        onClose={() => setIsVideoPackageModalOpen(false)}
        onConfirm={() => setIsVideoPackageModalOpen(false)}
        buttonLabels={{
          confirm: t('videoLibrary.packageModal.confirmButton'),
        }}
        icon={questionImg}>
        <h4>{t('videoLibrary.packageModal.title')}</h4>
        <p>{t('videoLibrary.packageModal.description1')}</p>
        <p>
          <Trans i18nKey="videoLibrary.packageModal.description2" components={{ 1: <b /> }} />
        </p>
      </Popup>
      <Alert
        handleClose={closeDeleteVideoAlert}
        open={deleteVideoAlertIsOpen}
        onConfirm={handleDeleteModule}
        cancelButton
        buttonLabel={t('videoLibrary.deleteAlert.delete')}
        dialogTitle={t('videoLibrary.deleteAlert.title')}
        dialogText={t('videoLibrary.deleteAlert.subtitle')}
        dialogContent={
          <Typography style={{ fontWeight: 'bold' }}>
            {t('videoLibrary.deleteAlert.message', {
              value: moduleForDeletion?.title,
            })}
          </Typography>
        }
      />
      <>
        {!isMultiSimulationSideMenuOpen &&
          filteredVideosPackages &&
          filteredVideosPackages.length > 0 &&
          !isTableView && (
            <PackagesCaruselView
              showPreview={handleVideosPackagePreview}
              packages={filteredVideosPackages}
              savedSimulationPackages={saved_asset_packages}
              saveSimulationPackageToggle={toggleSavedAssetPackage}
            />
          )}
      </>
      {isPendingVideos || isVideosPackagesPending ? (
        <LoadingContainer />
      ) : Object.values(filteredVideos).length ? (
        isTableView ? (
          <VideoLibraryTable
            openPreviewModal={handleOpenPreviewModal}
            deleteModule={openDeleteVideoAlert}
            launchModule={handleLaunchModule}
            saveModule={handleSaveModule}
            videosByTopic={filteredVideos}
            savedVideos={saved_assets}
            savedPackages={saved_asset_packages}
            saveSimulationPackageToggle={toggleSavedAssetPackage}
            queryFilters={queryFilters}
          />
        ) : (
          <VideoLibraryCardView
            openPreviewModal={handleOpenPreviewModal}
            deleteModule={openDeleteVideoAlert}
            launchModule={handleLaunchModule}
            saveModule={handleSaveModule}
            videosByTopic={filteredVideos}
            savedVideos={saved_assets}
          />
        )
      ) : (
        <NoResultsContainer text={t('videoLibrary.noResults')} />
      )}
    </Box>
  )
}

export default VideosLibrary
